import type { ProblemNewDraft } from '@/public/features/problem/types'
import { TOTAL_STEPS } from '@/public/features/problem/constants/problemNew'
import { buttonStyles } from '@/shared/ui/Button'
import { ApiError } from '@/shared/lib/http'
import { SERVICE_UNAVAILABLE_NOTICE } from '@/public/features/problem/constants/messages'
import { ROW_CLASS } from '@/shared/ui/buttonRow'

export function SectionHead({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-body-lg leading-[1.4] font-medium text-black-600">{title}</p>
      <p className="m-0 text-body-sm font-medium text-black-300">{desc}</p>
    </div>
  )
}
export function Field({
  label,
  placeholder,
  value,
  onChange,
  type = 'text',
  inputMode,
  autoComplete,
  error = null,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  type?: string
  inputMode?: 'numeric' | 'tel' | 'text'
  autoComplete?: string
  error?: string | null
}) {
  const errorId = `${label}-error`

  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-caption font-medium text-black-800">{label}</span>
      <input
        className={error !== null ? 'min-h-10 rounded-lg border bg-white-50 px-3 py-2.5 text-body-sm text-black-600 placeholder:text-[#808080] border-yellow-700' : 'min-h-10 rounded-lg border bg-white-50 px-3 py-2.5 text-body-sm text-black-600 placeholder:text-[#808080] border-white-400'}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        {...(inputMode !== undefined ? { inputMode } : {})}
        {...(autoComplete !== undefined ? { autoComplete } : {})}
        aria-invalid={error !== null}
        {...(error !== null ? { 'aria-describedby': errorId } : {})}
      />
      {error !== null && (
        <span id={errorId} className="text-label font-medium text-yellow-900" role="alert">
          {error}
        </span>
      )}
    </label>
  )
}
export function Buttons({ draft, rowClass = ROW_CLASS }: { draft: ProblemNewDraft; rowClass?: string }) {
  const isLast = draft.step === TOTAL_STEPS

  return (
    <div className={rowClass}>
      <button
        type="button"
        className={buttonStyles('lg', 'assistive', 'ui-pressable w-[90px] shrink-0 px-[22px] py-3')}
        onClick={draft.goPrev}
      >
        이전
      </button>
      <button
        type="button"
        className={buttonStyles('lg', 'primary', 'ui-pressable min-w-0 flex-1 shrink-0 grow basis-0')}
        disabled={!draft.canGoNext || draft.isSubmitting}
        onClick={isLast ? draft.submit : draft.goNext}
      >
        {isLast ? (draft.isSubmitting ? '등록하는 중…' : '시민 문제 등록하기') : '다음'}
      </button>
    </div>
  )
}

export function SubmitError({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null

  return (
    <div className="rounded-lg bg-white-200 p-3 text-body-sm text-black-600" role="alert">
      <b>등록하지 못했습니다.</b>
      {error instanceof ApiError && error.status === 503 ? (
        SERVICE_UNAVAILABLE_NOTICE
      ) : error instanceof ApiError && error.detail.length > 0 ? (
        <ul className="m-0 mt-1 flex list-disc flex-col gap-0.5 pl-5">
          {error.detail.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      ) : (
        ' 잠시 후 다시 시도해 주세요.'
      )}
    </div>
  )
}
